import { useEffect, useState } from "react";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Switch,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { useApi } from "../../hooks/useApi";

import LoadingComponent from "../../shared_modules/loadingComponent";
import InviteBarberDialog from "../../dialogs/InviteBarberDialog";
import EditBarberScheduleDialog from "../../dialogs/barberScheduleDialog";
import BarberScheduleDialog from "../../dialogs/barberScheduleDialog";
import BarberServicesDialog from "../../dialogs/barberServicesDialog";
import BarberExceptionsDialog from "../../dialogs/BarberExceptionsDialog";

import { formatDateTime, formatPhoneNumber } from "../../utils/functions";

import ContentCutIcon from "@mui/icons-material/ContentCut";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import TodayIcon from "@mui/icons-material/Today";

const Barbers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();

  const [barbers, setBarbers] = useState([]);
  const [pendingBarbers, setPendingBarbers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showInviteBarberDialog, setShowInviteBarberDialog] = useState(false);

  const [showBarberScheduleDialog, setShowBarberScheduleDialog] = useState({
    show: false,
    id: null,
  });

  const [showBarberServicesDialog, setShowBarberServicesDialog] = useState({
    show: false,
    id: null,
  });

  const [exceptionsBarber, setExceptionsBarber] = useState(null);

  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const retrieveBarbers = async () => {
    setIsLoading(true);

    try {
      const response = await api("/barbers/barbers.php", { method: "GET" });
      setBarbers(response.data.barbers);
      setPendingBarbers(response.data.pending_invites);
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    retrieveBarbers();
  }, []);

  const toggleBarberActive = async (barber) => {
    const newValue = barber.is_active === 1 ? 0 : 1;
    setBarbers((prev) =>
      prev.map((b) =>
        b.user_id === barber.user_id ? { ...b, is_active: newValue } : b,
      ),
    );
    try {
      await api("/barbers/barbers.php", {
        method: "PUT",
        body: JSON.stringify({ barber_id: barber.user_id, is_active: newValue }),
      });
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
      setBarbers((prev) =>
        prev.map((b) =>
          b.user_id === barber.user_id ? { ...b, is_active: barber.is_active } : b,
        ),
      );
    }
  };

  const renderPendingBarbers = () => {
    return (
      <>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Pending Invites
          </Typography>

          <Chip label={pendingBarbers.length} color={"info"} size="small" />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {pendingBarbers.map((barber) => (
            <Grid key={barber.id} size={{ xs: 12, md: 4 }}>
              <Box
                key={barber.id}
                sx={{
                  display: "flex",
                  width: "100%",
                  backgroundColor: "offWhite",
                  px: 2.5,
                  py: 2.25,
                  borderRadius: 2,
                  border: `0.1px solid`,
                  borderColor: "customDivider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {barber.first_name} {barber.last_name}
                    </Typography>
                    <Chip
                      label={
                        barber?.status?.charAt(0)?.toUpperCase() +
                        barber?.status?.slice(1)
                      }
                      color={"info"}
                      size="small"
                    />
                  </Box>

                  <Typography variant="subtitle2">{barber.email}</Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mt: 1,
                      gap: 1,
                    }}
                  >
                    <Typography variant="caption">Invited:</Typography>

                    <Typography variant="body2">
                      {formatDateTime(barber.created_at)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mt: 1,
                      gap: 1,
                    }}
                  >
                    <Typography variant="caption">Expires:</Typography>

                    <Typography variant="body2">
                      {formatDateTime(barber.expires_at)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 3,
                      gap: 1,
                    }}
                  >
                    <Button
                      startIcon={<RestartAltIcon />}
                      variant="text"
                      size="small"
                      color="info"
                      sx={{
                        border: "1px solid",
                        borderColor: "info",
                        boxShadow: 1,
                      }}
                    >
                      Resend Invite
                    </Button>

                    <Button
                      startIcon={<ClearIcon />}
                      variant="text"
                      color="error"
                      size="small"
                      sx={{
                        border: "1px solid",
                        borderColor: "error",
                        boxShadow: 1,
                      }}
                    >
                      Revoke Invite
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </>
    );
  };

  const BarberCard = ({ barber }) => (
    <Grid size={{ xs: 12, md: 4 }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          backgroundColor: "offWhite",
          px: 2.5,
          pt: 3,
          pb: 2.5,
          borderRadius: 2,
          border: `0.1px solid`,
          borderColor: "customDivider",
          opacity: barber.is_active === 1 ? 1 : 0.6,
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
        }}
      >
        {/* Toggle — top right */}
        <Switch
          size="small"
          checked={barber.is_active === 1}
          onChange={() => toggleBarberActive(barber)}
          sx={{ position: "absolute", top: 12, right: 12 }}
        />

        {/* Centered avatar */}
        <Avatar
          src={barber.avatar_url || undefined}
          sx={{ width: 72, height: 72, fontSize: 22, fontWeight: 600, backgroundColor: "grey.200", color: "text.primary", mb: 1.5 }}
        >
          {!barber.avatar_url && `${barber.first_name?.[0] ?? ""}${barber.last_name?.[0] ?? ""}`}
        </Avatar>

        {/* Name + contact */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "center" }}>
          {barber.first_name} {barber.last_name}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", display: "block" }}>
          {barber.email}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center", display: "block", mb: 2.5 }}>
          {formatPhoneNumber(barber.phone)}
        </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, width: "100%" }}>
            <Button
              startIcon={<ScheduleIcon />}
              variant="text"
              size="small"
              sx={{ border: "1px solid", borderColor: "customDivider", boxShadow: 1 }}
              onClick={() => setShowBarberScheduleDialog({ show: true, id: barber.user_id })}
            >
              Schedule
            </Button>
            <Button
              startIcon={<ContentCutIcon />}
              variant="text"
              size="small"
              sx={{ border: "1px solid", borderColor: "customDivider", boxShadow: 1 }}
              onClick={() => setShowBarberServicesDialog({ show: true, id: barber.user_id })}
            >
              Services
            </Button>
            <Button
              startIcon={<TodayIcon />}
              variant="text"
              size="small"
              sx={{ border: "1px solid", borderColor: "customDivider", boxShadow: 1 }}
              onClick={() => setExceptionsBarber(barber)}
            >
              Exceptions
            </Button>
          </Box>
      </Box>
    </Grid>
  );

  const renderBarbers = () => {
    const active = barbers.filter((b) => b.is_active === 1);
    const inactive = barbers.filter((b) => b.is_active !== 1);

    return (
      <>
        {active.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Active ({active.length})
            </Typography>
            <Grid container spacing={2}>
              {active.map((barber) => <BarberCard key={barber.user_id} barber={barber} />)}
            </Grid>
          </Box>
        )}

        {inactive.length > 0 && (
          <Box>
            <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
              Inactive ({inactive.length})
            </Typography>
            <Grid container spacing={2}>
              {inactive.map((barber) => <BarberCard key={barber.user_id} barber={barber} />)}
            </Grid>
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <InviteBarberDialog
        showInviteBarberDialog={showInviteBarberDialog}
        setShowInviteBarberDialog={setShowInviteBarberDialog}
        setShowInviteAlert={setShowAlert}
        onSave={retrieveBarbers}
      />

      <BarberScheduleDialog
        showBarberScheduleDialog={showBarberScheduleDialog.show}
        setShowBarberScheduleDialog={setShowBarberScheduleDialog}
        setShowBarberScheduleAlert={setShowAlert}
        selectedBarber={showBarberScheduleDialog.id}
        onSave={retrieveBarbers}
      />

      <BarberServicesDialog
        showBarberServicesDialog={showBarberServicesDialog.show}
        setShowBarberServicesDialog={setShowBarberServicesDialog}
        setShowBarberServicesAlert={setShowAlert}
        selectedBarber={showBarberServicesDialog.id}
        onSave={retrieveBarbers}
      />

      <BarberExceptionsDialog
        open={!!exceptionsBarber}
        onClose={() => setExceptionsBarber(null)}
        barber={exceptionsBarber}
      />

      <Box
        sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, pt: 5, pb: 10 }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant={isMobile ? "h4" : "h3"}>Barbers</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Manage your team and their profiles.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={() => setShowInviteBarberDialog(true)}
            >
              Invite Barber
            </Button>
          </Box>
        </Box>

        {showAlert.show === true && (
          <Alert
            sx={{ mb: 2 }}
            severity={showAlert.type}
            onClose={() => {
              setShowAlert({ show: false, message: "", type: "" });
            }}
          >
            {showAlert.message}
          </Alert>
        )}

        {isLoading && <LoadingComponent marginTop={10} />}

        {!isLoading &&
          pendingBarbers &&
          pendingBarbers.length > 0 &&
          renderPendingBarbers()}

        {!isLoading && (
          <>
            {barbers && barbers.length > 0 ? (
              renderBarbers()
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  No barbers yet. Invite a barber to see your team.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default Barbers;
